import { IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { PackageClient } from '@frontend/package/api';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { Slot } from '@frontend/slot/types';
import { TransactionActorClient, TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import React, { useCallback, useRef, useState } from 'react';

import { useAppSelector } from '../../redux/store';
import { useLicenseRepository } from '../../utils/license-repository';
import { useIdentification } from '../useIdentification/useIdentification';
import { useTransactionListener } from '../useTransactionListener/useTransactionListener';

export interface CreateTransactionParams {
    accountId: string;
    productId: string;
    equipmentId?: string;
}

export interface UseAuditTransactionOptions {
    id: string;
    application: Application;
    feature: Feature;
    slot: Slot | null;
    onComplete: () => void;
}

export const useAuditTransaction = (options: UseAuditTransactionOptions) => {
    const { id, slot, onComplete } = options;
    const navigationState = useAppSelector((store) => store.navigation);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<React.ReactNode | null>(null);
    const pendingTransactionIdRef = useRef<string | null>(null);
    const { getFunctionalitySetting, getFunctionality } = useLicenseRepository();
    const spotsLayoutSettings = getFunctionalitySetting(getFunctionality(options.application, options.feature)?.id ?? '');

    const createTransactionWithPackage = useCallback(
        async (params: CreateTransactionParams) => {
            if (!slot || isSubmitting) return;
            if (!spotsLayoutSettings?.transaction_workflow_id || !spotsLayoutSettings?.drop_off_package_workflow_id) {
                setError(CommonMessage.ERRORS.DEFAULT);
                return;
            }
            setIsSubmitting(true);
            try {
                const transaction = await TransactionClient.postTransaction(params.accountId, {
                    type: TransactionType.AUDIT,
                    user_id: navigationState.user?.id ?? null,
                    spot_id: slot.spot_id,
                    module_id: slot.module_id,
                    slot_id: slot.id,
                    application: options.application,
                    feature: options.feature,
                    workflow_id: (spotsLayoutSettings?.transaction_workflow_id ?? undefined) as string | undefined,
                    state: TransactionStateName.TRANSACTION_CREATED
                });

                await PackageClient.postPackage(transaction.account_id, transaction.id, {
                    type: PackageType.DROP_OFF,
                    product_id: params.productId,
                    equipment_id: params.equipmentId,
                    spot_id: slot.spot_id,
                    module_id: slot.module_id,
                    slot_id: slot.id,
                    quantity: 1,
                    workflow_id: (spotsLayoutSettings?.drop_off_package_workflow_id ?? undefined) as string | undefined,
                    user_id: navigationState.user?.id ?? null,
                    state: PackageStateName.PACKAGE_CREATED,
                    application: options.application,
                    feature: options.feature
                });

                if (navigationState.user?.id) {
                    TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                        type: TransactionActorType.DROPPED_OFF_BY,
                        user_id: navigationState.user.id
                    });
                }

                pendingTransactionIdRef.current = transaction.id;

                await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                    trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                });
            } catch {
                setError(CommonMessage.ERRORS.DEFAULT);
                setIsSubmitting(false);
            }
        },
        [slot, isSubmitting, spotsLayoutSettings, navigationState.user?.id]
    );

    useTransactionListener({
        id,
        onComplete: (transaction) => {
            if (pendingTransactionIdRef.current && transaction.id === pendingTransactionIdRef.current) {
                pendingTransactionIdRef.current = null;
                setIsSubmitting(false);
                onComplete();
            }
        }
    });

    useIdentification({
        id,
        onEquipmentIdentified: (equipment: IEquipment) => {
            createTransactionWithPackage({
                accountId: equipment.account_id,
                productId: equipment.product_id,
                equipmentId: equipment.id
            });
        }
    });

    return {
        isSubmitting,
        error,
        setError,
        createTransactionWithPackage
    };
};
